import type { JSX, ReactNode } from "react";

import { Box, Stack, Text } from "@mantine/core";

import { useSalesContext } from "../../../../sales-context/SalesContext";
import { mapPartnerToDisplayName } from "./OffersView.helpers";

function DetailField({ label, children, className }: { label: string; children: ReactNode; className?: string }) {
    return (
        <Stack gap="4xs">
            <Text size="xs" c="gray.5">
                {label}
            </Text>
            <Text size="sm" c="gray.1" className={className}>
                {children}
            </Text>
        </Stack>
    );
}

export function DeviceInsights(): JSX.Element | null {
    const formatPhoneNumberDisplay = (phoneNumber: string): string => {
        if (phoneNumber.length <= 3) return `(${phoneNumber}`;
        if (phoneNumber.length <= 6) return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
        return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
    };

    const { ivrInfo, partner } = useSalesContext();
    const { productTitle, orderNumber, brandPhoneNumber, supportPhoneNumber, summary, intent, device, accountId } =
        ivrInfo || {};

    if (!productTitle && !summary && !intent && !device && !accountId) return null;

    return (
        <Box
            bg="dark.5"
            m="sm"
            style={{
                borderRadius: "var(--mantine-radius-md)",
                maxWidth: 360,
            }}
        >
            <Stack p="sm">
                {productTitle ? <DetailField label="Product details">{productTitle}</DetailField> : null}

                {orderNumber ? (
                    <DetailField label="Order number" className="fs-mask">
                        {orderNumber}
                    </DetailField>
                ) : null}

                {brandPhoneNumber || supportPhoneNumber ? (
                    <Stack gap="4xs">
                        <Text size="xs" c="gray.5">
                            Support phone numbers
                        </Text>
                        <Stack gap="0">
                            {brandPhoneNumber ? (
                                <Text size="sm" c="gray.1">
                                    Brand/Manufacturer: {formatPhoneNumberDisplay(brandPhoneNumber)}
                                </Text>
                            ) : null}
                            {supportPhoneNumber ? (
                                <Text size="sm" c="gray.1">
                                    {mapPartnerToDisplayName(partner)}: {formatPhoneNumberDisplay(supportPhoneNumber)}
                                </Text>
                            ) : null}
                        </Stack>
                    </Stack>
                ) : null}

                {accountId ? (
                    <DetailField label="Account Number" className="fs-mask">
                        {accountId}
                    </DetailField>
                ) : null}

                {intent ? <DetailField label="Intent">{intent}</DetailField> : null}

                {device ? <DetailField label="Device">{device}</DetailField> : null}

                {summary ? <DetailField label="Summary">{summary}</DetailField> : null}
            </Stack>
        </Box>
    );
}
